import { fetchLoadable } from '@/store/utils'
import { runCloudFunction } from '@/store/parseUtils'
import type { Study } from '@pocketprep/types'
import { stripeModule } from '@/store/stripe/module'
import { type Stripe as StripeNode } from 'stripe'
import { userModule } from '@/store/user/module'
import { analyticsModule } from '@/store/analytics/module'
import { loadStripe } from '@stripe/stripe-js'
import { resetLoadable } from '@/store/utils'

const deleteStripePM = async (paymentMethodId: string) => {
    await runCloudFunction<Study.Cloud.deletePaymentMethod>('deletePaymentMethod', { paymentMethodId });
    (await stripeModule.state.payment.value).paymentMethods = (await stripeModule.state.payment.value)
        .paymentMethods.filter(pm => pm.id !== paymentMethodId)
}

const updateStripeSubscription = async (params: {
    paymentMethodId?: string
    planId: string
    subscriptionId: string
    planChanged: boolean
}) => {
    await runCloudFunction<Study.Cloud.updateSubscription>('updateSubscription', params)
    await Promise.all([
        fetchStripeSubscriptions(true),
        userModule.actions.fetchUserData({ stripeSubs: true }),
    ])
    analyticsModule.actions.updateIntercom()
}

const fetchStripeSubscriptions = async (forceFetch?: boolean) => {
    const user = userModule.state.user
    const customerId = user?.stripeCustomerId

    if (!user) {
        throw new Error('Unable to find user.')
    }

    if (!customerId) {
        return
    }
    
    await fetchLoadable(
        stripeModule.state.subscriptions, 
        () => runCloudFunction<Study.Cloud.fetchStripeSubscriptions>('fetchStripeSubscriptions', { customerId }),
        forceFetch
    )
}

const fetchPaymentMethods = async (forceFetch?: boolean) => {
    await fetchLoadable(
        stripeModule.state.payment, 
        () => runCloudFunction<Study.Cloud.fetchPaymentMethods>('fetchPaymentMethods'),
        forceFetch
    )
}

type TfetchStripePrices = () => StripeNode.Price[]

const fetchStripePrices = async () => {
    await fetchLoadable(stripeModule.state.prices, async () => {
        return runCloudFunction<TfetchStripePrices>('fetchStripePrices')
    })
}

const loadStripeState = async () => {
    if (import.meta.env.VUE_APP_STRIPE_PUBLISHABLE_KEY) {
        await fetchStripePrices()
        const stripe = await loadStripe(import.meta.env.VUE_APP_STRIPE_PUBLISHABLE_KEY)
        if (stripe) {
            stripeModule.state.stripe = stripe
            return { stripe }
        }
    }

    return {
        stripe: null,
    }
}

type TCreateCheckoutSession = (params: {
    mode: StripeNode.Checkout.SessionCreateParams.Mode
    priceId?: string
    priceData?: StripeNode.Checkout.SessionCreateParams.LineItem.PriceData
    currency?: StripeNode.Checkout.SessionCreateParams['currency']
    quantity?: number
    referralId?: string
    impactClickId?: string
    paymentPlatform?: string
}) => Promise<{
    clientSecret: string | null
    checkoutSessionId: string
}>

const createCheckoutSession = async ({ mode, priceId, referralId, impactClickId, paymentPlatform }: {
    mode: 'subscription' | 'payment' | 'setup'
    priceId?: string
    referralId?: string
    impactClickId?: string
    paymentPlatform: string
}) => {
    const sessionCreateParams: Parameters<TCreateCheckoutSession>[0] = {
        mode,
        priceId,
        referralId,
        impactClickId,
        paymentPlatform,
    }

    if (mode === 'setup') {
        // currency is a required parameter in setup mode
        sessionCreateParams.currency = 'usd'
    }

    const {
        clientSecret,
        checkoutSessionId,
    } = await runCloudFunction<TCreateCheckoutSession>('createCheckoutSession', sessionCreateParams)

    return {
        clientSecret,
        checkoutSessionId,
    }
}

type TFetchCheckoutSession = (params: {
    checkoutSessionId: string
}) => Promise<StripeNode.Checkout.Session>

const fetchCheckoutSession = async ({ checkoutSessionId }: { checkoutSessionId: string }) => {
    const checkoutSession = await runCloudFunction<TFetchCheckoutSession>('fetchCheckoutSession', {
        checkoutSessionId,
    })

    return checkoutSession
}

const loadNewPaymentMethods = async () => {
    resetLoadable(stripeModule.state.payment)
    await stripeModule.actions.fetchPaymentMethods()
}

export default {
    fetchStripePrices,
    fetchPaymentMethods,
    fetchStripeSubscriptions,
    updateStripeSubscription,
    deleteStripePM,
    loadStripeState,
    createCheckoutSession,
    fetchCheckoutSession,
    loadNewPaymentMethods,
}
